import React, { useCallback, useEffect, useState, useMemo } from 'react';
import API_CONFIG from '../API_CONFIG/API_CONFIG';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Fade } from 'react-awesome-reveal';
import { FaSearch, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';

// Palette de couleurs luxe (or, vert, noir)
const colors = {
  gold: '#C69C6D',
  goldHover: '#D4AF37',
  green: '#2A7F6E',
  greenLight: '#E8F5F2',
  dark: '#1a1a1a',
  textLight: '#F5F0EB',
  textMuted: '#B7A99A',
  glassBg: 'rgba(255, 255, 255, 0.1)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  shadow: '0 8px 32px rgba(0,0,0,0.2)',
};

// Variantes d'animation pour les cartes
const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.08,
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
  hover: {
    scale: 1.04,
    boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

// Composant de chargement
const Loader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
    >
      <FaSpinner size={40} color={colors.gold} />
    </motion.div>
  </div>
);

// Composant d'erreur
const ErrorMessage = ({ message }) => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
    <div className="text-center">
      <FaExclamationTriangle size={40} color={colors.gold} />
      <p className="mt-3 text-light">{message || 'Une erreur est survenue. Veuillez réessayer.'}</p>
      <button className="btn btn-outline-light mt-2" onClick={() => window.location.reload()}>
        Réessayer
      </button>
    </div>
  </div>
);

const AllMenu = ({ id_cat }) => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 12;

  // Récupération des données
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const uniqueParam = new Date().getTime();
      const response = await axios.get(
        `${API_CONFIG.apiUrl}CategorieProduit.php?id_cat=${id_cat}&unique=${uniqueParam}`
      );
      setData(response.data || []);
    } catch (err) {
      setError("Impossible de charger les produits. Vérifiez votre connexion.");
      console.error('Erreur :', err);
    } finally {
      setLoading(false);
    }
  }, [id_cat]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrage et pagination (mémorisés)
  const filteredData = useMemo(() => {
    return data.filter(item =>
      item.produit?.toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    return filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredData, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section
      className="py-5"
      style={{
        background: `linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.8) 100%), url(images/assets/ban5.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '25px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
        minHeight: '500px',
      }}
    >
      <div className="container">
        {/* Barre de recherche avec icône */}
        <div className="mb-5 d-flex justify-content-center">
          <div className="position-relative w-50" style={{ maxWidth: '500px' }}>
            <FaSearch
              style={{
                position: 'absolute',
                left: '18px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.gold,
                opacity: 0.7,
              }}
            />
            <input
              type="text"
              className="form-control rounded-pill px-5 py-3 shadow"
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                border: `2px solid ${colors.gold}`,
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(5px)',
                fontSize: '1rem',
                transition: 'all 0.3s',
              }}
              onFocus={(e) => (e.target.style.boxShadow = `0 0 0 4px ${colors.gold}40`)}
              onBlur={(e) => (e.target.style.boxShadow = 'none')}
            />
          </div>
        </div>

        {/* États de chargement et erreur */}
        {loading ? (
          <Loader />
        ) : error ? (
          <ErrorMessage message={error} />
        ) : (
          <>
            <Fade cascade damping={0.15} triggerOnce>
              <div className="row g-4">
                <AnimatePresence>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((categorie, index) => (
                      <motion.div
                        key={categorie.id_produit || index}
                        className="col-sm-6 col-md-4 col-lg-3"
                        custom={index}
                        initial="hidden"
                        animate="visible"
                        exit={{ opacity: 0, scale: 0.8 }}
                        variants={cardVariants}
                      >
                        <motion.div
                          variants={cardVariants}
                          whileHover="hover"
                          className="card h-100 border-0 rounded-4 overflow-hidden shadow-sm"
                          style={{
                            background: colors.glassBg,
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            border: `1px solid ${colors.glassBorder}`,
                            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <div className="overflow-hidden" style={{ height: '200px', backgroundColor: '#1a1a1a' }}>
                            <motion.img
                              src={`${API_CONFIG.apiUrl}pictures/${categorie.pictures}`}
                              alt={categorie.produit}
                              className="w-100 h-100 object-fit-cover"
                              whileHover={{ scale: 1.1 }}
                              transition={{ duration: 0.6 }}
                              style={{ objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = '/images/placeholder.jpg'; // Image par défaut
                              }}
                            />
                          </div>
                          <div className="card-body text-center d-flex flex-column justify-content-between p-3">
                            <h6 className="card-title fw-bold text-light mb-2 text-truncate" style={{ color: colors.textLight }}>
                              {categorie.produit}
                            </h6>
                            <motion.div
                              className="badge rounded-pill py-2 px-3 mx-auto"
                              style={{
                                background: `linear-gradient(135deg, ${colors.gold}, ${colors.goldHover})`,
                                color: '#1a1a1a',
                                fontWeight: 600,
                                fontSize: '1rem',
                                boxShadow: '0 4px 12px rgba(198,156,109,0.4)',
                              }}
                              whileHover={{ scale: 1.08 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              ${categorie.prixT}
                            </motion.div>
                          </div>
                        </motion.div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="col-12 text-center text-light py-5">
                      <p>Aucun produit trouvé pour "{search}"</p>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </Fade>

            {/* Pagination améliorée */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <ul className="pagination pagination-sm gap-2">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link border-0 rounded-circle"
                      style={{
                        width: '38px',
                        height: '38px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: colors.textLight,
                        backdropFilter: 'blur(4px)',
                        border: `1px solid ${colors.gold}`,
                      }}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ‹
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                      <button
                        className="page-link border-0 rounded-circle"
                        style={{
                          width: '38px',
                          height: '38px',
                          backgroundColor: page === currentPage ? colors.gold : 'rgba(255,255,255,0.05)',
                          color: page === currentPage ? '#1a1a1a' : colors.textLight,
                          fontWeight: page === currentPage ? 700 : 400,
                          backdropFilter: 'blur(4px)',
                          border: `1px solid ${page === currentPage ? colors.gold : 'rgba(255,255,255,0.1)'}`,
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link border-0 rounded-circle"
                      style={{
                        width: '38px',
                        height: '38px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: colors.textLight,
                        backdropFilter: 'blur(4px)',
                        border: `1px solid ${colors.gold}`,
                      }}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      ›
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default AllMenu;